import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch } from '../api';
import logoOzon from '../logo_ozon_1.webp';

type PageKey = 'dashboard' | 'ai_agents' | 'ozon_supplies' | 'tables' | 'settings' | 'billing';

interface Props {
  active: PageKey;
  onNavigate: (page: PageKey) => void;
  onLogout: () => void;
  roleLevel: number;
}

const navItems: { key: PageKey; label: string }[] = [
  { key: 'dashboard', label: 'Дашборд' },
  { key: 'ai_agents', label: 'AI Агент' },
  { key: 'ozon_supplies', label: 'Поставки OZON' },
  { key: 'tables', label: 'Таблицы' },
  { key: 'settings', label: 'Настройки' },
  { key: 'billing', label: 'Оплата' },
];

const OWNER_LEVEL = 15;
const ADMIN_LEVEL = 10;
const MANAGER_LEVEL = 5;

const TopNav: React.FC<Props> = ({ active, onNavigate, onLogout, roleLevel }) => {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const roleLabels: Record<string, string> = {
    Owner: 'Владелец',
    Admin: 'Администратор',
    Manager: 'Менеджер',
    User: 'Пользователь',
    owner: 'Владелец',
    admin: 'Администратор',
    manager: 'Менеджер',
    user: 'Пользователь',
  };
  const userEmail = useMemo(() => localStorage.getItem('userEmail') || '—', []);
  const [storeName, setStoreName] = useState('');
  const userRole = useMemo(() => {
    const role = localStorage.getItem('userRole');
    if (role) return roleLabels[role] || role;
    const level = localStorage.getItem('userRoleLevel');
    if (!level) return '—';
    const numeric = Number(level);
    if (numeric >= OWNER_LEVEL) return roleLabels.owner;
    if (numeric >= ADMIN_LEVEL) return roleLabels.admin;
    if (numeric >= MANAGER_LEVEL) return roleLabels.manager;
    return roleLabels.user;
  }, []);

  const allowedNavItems = navItems.filter(item => {
    if (item.key === 'settings') return roleLevel >= OWNER_LEVEL;
    if (item.key === 'tables') return roleLevel >= ADMIN_LEVEL;
    // Billing surface is Owner+Admin (D25). Admin views, Owner edits;
    // the page itself does fine-grained gating on action buttons.
    if (item.key === 'billing') return roleLevel >= ADMIN_LEVEL;
    return true;
  });

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const load = () => {
      setStoreName(localStorage.getItem('storeName') || '');
    };
    load();
    const onStoreNameChanged = () => load();
    const onStorage = (event: StorageEvent) => {
      if (event.key === 'storeName') load();
    };
    window.addEventListener('storeNameChanged', onStoreNameChanged);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('storeNameChanged', onStoreNameChanged);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  // Active supply jobs polling
  interface ActiveJob {
    job_id: string;
    job_kind: string;
    status: string;
    label: string;
    priority_score: number;
    queue_position: number;
    progress: { stage: string; percent: number; message: string };
  }
  const [activeJobs, setActiveJobs] = useState<ActiveJob[]>([]);
  const [jobsPopupOpen, setJobsPopupOpen] = useState(false);
  const [showSystemJobs, setShowSystemJobs] = useState(false);
  const jobsRef = useRef<HTMLDivElement | null>(null);

  const loadActiveJobs = useCallback(async () => {
    try {
      const res = await apiFetch('/api/ai/jobs/active-supply');
      if (res.ok) {
        const data = await res.json();
        setActiveJobs(Array.isArray(data.jobs) ? data.jobs : []);
      }
    } catch {
      // Silent — don't break nav on API failure
    }
  }, []);

  useEffect(() => {
    loadActiveJobs();
    const id = window.setInterval(loadActiveJobs, 5000);
    return () => window.clearInterval(id);
  }, [loadActiveJobs]);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (jobsRef.current && !jobsRef.current.contains(event.target as Node)) {
        setJobsPopupOpen(false);
      }
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  return (
    <div className="sticky top-0 z-40 border-b border-slate-200 bg-white">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3">
        <div className="flex items-center gap-3 text-slate-900">
          <img src={logoOzon} alt="Ozon" className="h-8 w-14 object-contain" />
          <span className="text-base font-semibold tracking-wide truncate max-w-[240px]" title={storeName || 'Ozon'}>
            {storeName || 'Ozon'}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center rounded-full bg-slate-100 p-1">
            {allowedNavItems.map(item => (
              <button
                key={item.key}
                className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium transition ${
                  active === item.key
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
                onClick={() => onNavigate(item.key)}
              >
                {item.key === 'dashboard' && (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                    <rect x="3" y="3" width="8" height="8" rx="2" />
                    <rect x="13" y="3" width="8" height="5" rx="2" />
                    <rect x="13" y="10" width="8" height="11" rx="2" />
                    <rect x="3" y="13" width="8" height="8" rx="2" />
                  </svg>
                )}
                {item.key === 'tables' && (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                    <rect x="3" y="5" width="18" height="14" rx="2" />
                    <path d="M3 10h18M8 5v14M16 5v14" />
                  </svg>
                )}
                {item.key === 'ai_agents' && (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                    <rect x="7" y="8" width="10" height="9" rx="2" />
                    <path d="M9 13h.01M15 13h.01" />
                    <path d="M12 8V5M9 17v2M15 17v2M5 11h2M17 11h2" />
                  </svg>
                )}
                {item.key === 'ozon_supplies' && (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                    <path d="M3 7.5 12 3l9 4.5-9 4.5L3 7.5Z" />
                    <path d="M3 12.5 12 17l9-4.5" />
                    <path d="M3 17.5 12 22l9-4.5" />
                  </svg>
                )}
                {item.key === 'settings' && (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                    <circle cx="12" cy="12" r="3.2" />
                    <path d="M19.4 15a1 1 0 0 0 .2 1.1l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1 1 0 0 0-1.1-.2 1 1 0 0 0-.6.9V20a2 2 0 1 1-4 0v-.1a1 1 0 0 0-.6-.9 1 1 0 0 0-1.1.2l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1 1 0 0 0 .2-1.1 1 1 0 0 0-.9-.6H4a2 2 0 1 1 0-4h.1a1 1 0 0 0 .9-.6 1 1 0 0 0-.2-1.1l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1 1 0 0 0 1.1.2 1 1 0 0 0 .6-.9V4a2 2 0 1 1 4 0v.1a1 1 0 0 0 .6.9 1 1 0 0 0 1.1-.2l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1 1 0 0 0-.2 1.1 1 1 0 0 0 .9.6H20a2 2 0 1 1 0 4h-.1a1 1 0 0 0-.9.6Z" />
                  </svg>
                )}
                <span>{item.label}</span>
              </button>
            ))}
          </div>
          {/* Active jobs indicator */}
          {(() => {
            const hasJobs = activeJobs.length > 0;
            const userJobsCount = activeJobs.filter(j => j.priority_score >= 90).length;
            const badgeIsUser = userJobsCount > 0;
            return (
            <div className="relative" ref={jobsRef}>
              <button
                type="button"
                className={`relative flex h-8 w-8 items-center justify-center rounded-full border transition-colors ${
                  badgeIsUser
                    ? 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100'
                    : hasJobs
                      ? 'border-slate-200 bg-slate-50 text-slate-500 hover:bg-slate-100'
                      : 'border-slate-200 text-slate-400 hover:bg-slate-50 hover:text-slate-600'
                }`}
                onClick={() => setJobsPopupOpen(!jobsPopupOpen)}
                title={hasJobs ? `Активные задачи (${activeJobs.length})` : 'Задачи'}
              >
                {badgeIsUser ? (
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-400 opacity-75" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-blue-500" />
                  </span>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                  </svg>
                )}
                {hasJobs && (
                  <span className={`absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[9px] font-bold text-white ${
                    badgeIsUser ? 'bg-blue-600' : 'bg-slate-400'
                  }`}>
                    {activeJobs.length}
                  </span>
                )}
              </button>
              {jobsPopupOpen && (() => {
                const USER_PRIORITY_THRESHOLD = 90;
                const userJobs = activeJobs.filter(j => j.priority_score >= USER_PRIORITY_THRESHOLD);
                const systemJobs = activeJobs.filter(j => j.priority_score < USER_PRIORITY_THRESHOLD);
                const visibleJobs = showSystemJobs ? activeJobs : userJobs;
                return (
                <div className="absolute right-0 mt-2 w-80 rounded-xl border border-slate-200 bg-white shadow-lg z-50">
                  <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
                    <div className="text-xs font-semibold text-slate-700">
                      {showSystemJobs ? 'Все задачи' : 'Мои задачи'} ({visibleJobs.length})
                    </div>
                    {systemJobs.length > 0 && (
                      <button
                        type="button"
                        onClick={e => { e.stopPropagation(); setShowSystemJobs(!showSystemJobs); }}
                        className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium transition ${
                          showSystemJobs
                            ? 'bg-slate-200 text-slate-700'
                            : 'bg-slate-100 text-slate-400 hover:text-slate-600'
                        }`}
                        title={showSystemJobs ? 'Скрыть системные задачи' : `Показать системные (+${systemJobs.length})`}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="3" />
                          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                        </svg>
                        {showSystemJobs ? 'Все' : `+${systemJobs.length}`}
                      </button>
                    )}
                  </div>
                  {visibleJobs.length === 0 ? (
                    <div className="px-4 py-6 text-center text-xs text-slate-400">
                      {userJobs.length === 0 && systemJobs.length > 0
                        ? 'Ваших задач нет. Нажмите ⚙ для системных.'
                        : 'Нет активных задач'}
                    </div>
                  ) : (
                  <div className="max-h-72 overflow-y-auto divide-y divide-slate-100">
                    {visibleJobs.map(job => {
                      const isSystem = job.priority_score < USER_PRIORITY_THRESHOLD;
                      return (
                      <div key={job.job_id} className={`px-4 py-3 ${isSystem ? 'opacity-70' : ''}`}>
                        <div className="flex items-center gap-2">
                          {job.status === 'running' ? (
                            <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600" />
                          ) : (
                            <div className="flex h-3.5 w-3.5 items-center justify-center rounded-full bg-amber-100 text-[8px] text-amber-700">⏳</div>
                          )}
                          <span className="text-sm font-medium text-slate-800 truncate">{job.label}</span>
                          {isSystem && <span className="text-[9px] text-slate-400">авто</span>}
                        </div>
                        {job.status === 'running' && job.progress.message ? (
                          <div className="mt-1.5" style={{ marginLeft: '22px' }}>
                            <div className="flex items-center justify-between text-[11px] text-slate-500">
                              <span className="truncate">{job.progress.message}</span>
                              <span className="ml-2 font-medium">{Math.round(job.progress.percent)}%</span>
                            </div>
                            <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                              <div
                                className="h-full rounded-full bg-blue-500 transition-all duration-500"
                                style={{ width: `${Math.min(100, Math.max(1, job.progress.percent))}%` }}
                              />
                            </div>
                          </div>
                        ) : job.status === 'queued' ? (
                          <div className="mt-1 text-[11px] text-slate-400" style={{ marginLeft: '22px' }}>
                            В очереди{job.queue_position > 0 ? ` (${job.queue_position}-я позиция)` : ''}
                          </div>
                        ) : null}
                      </div>
                      );
                    })}
                  </div>
                  )}
                </div>
                );
              })()}
            </div>
            );
          })()}
          <button
              type="button"
              className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-600 hover:bg-blue-100 transition-colors"
              onClick={() => {
                const token = localStorage.getItem('token') || '';
                window.location.href = `${location.protocol}//${location.hostname}:3002?token=${encodeURIComponent(token)}`;
              }}
              title="Переключиться на новый интерфейс V2"
            >
              V2 →
            </button>
          <div className="relative" ref={menuRef}>
            <button
              className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-500 hover:text-slate-800"
              onClick={() => setOpen(!open)}
              aria-label="User menu"
              title="User"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                <circle cx="12" cy="8" r="3.5" />
                <path d="M4 20c1.6-3.5 5-5.5 8-5.5S18.4 16.5 20 20" />
              </svg>
            </button>
            {open && (
              <div className="absolute right-0 mt-2 w-40 rounded-xl border border-slate-200 bg-white shadow-lg">
              <div className="px-4 py-3 border-b border-slate-100">
                  <div className="text-xs text-slate-500">Почта</div>
                  <div className="text-sm font-medium text-slate-800 truncate">{userEmail}</div>
                  <div className="mt-2 text-xs text-slate-500">Роль</div>
                  <div className="text-sm font-medium text-slate-800">{userRole}</div>
                </div>
                <button
                  className="flex w-full items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                  onClick={onLogout}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                    <path d="M9 6H5a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h4" />
                    <path d="M16 17l5-5-5-5" />
                    <path d="M21 12H9" />
                  </svg>
                  Выйти
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopNav;
